import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, MessageCircle } from "lucide-react";
import axios from 'axios';
import { showToast } from '@/utils/toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = '/api';

/**
 * SupportModal - Support ticket creation and history
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility state
 * @param {Function} props.onClose - Close modal callback
 * @param {string} props.supportTab - Active tab ('new' or 'tickets')
 * @param {Function} props.onTabChange - Tab change callback
 * @param {Object} props.user - Current user object
 */
const SupportModal = React.memo(({ 
  isOpen, 
  onClose, 
  supportTab, 
  onTabChange,
  user
}) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  useEffect(() => {
    if (isOpen && supportTab === 'tickets' && user) {
      loadTickets();
    }
  }, [isOpen, supportTab, user]);

  const loadTickets = async () => {
    if (!user) return;
    setLoadingTickets(true);
    try {
      const response = await axios.get(`${API}/support/tickets/${user.id}`);
      setTickets(response.data || []);
    } catch (error) {
      showToast.error("Destek talepleri yüklenemedi");
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message || !user) return;

    setLoading(true);
    try {
      await axios.post(`${API}/support/ticket`, {
        user_id: user.id,
        subject,
        message,
        category
      });
      
      showToast.success("Destek talebiniz alındı!");
      setSubject("");
      setMessage("");
      setCategory("general");
      onTabChange('tickets');
      loadTickets();
    } catch (error) {
      showToast.error("Destek talebi gönderilemedi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl" aria-label="Destek">
        <DialogHeader>
          <DialogTitle>Destek Merkezi</DialogTitle>
        </DialogHeader>
        
        <Tabs value={supportTab} onValueChange={onTabChange}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="new">Yeni Talep</TabsTrigger>
            <TabsTrigger value="tickets">Taleplerim</TabsTrigger>
          </TabsList>
          
          <TabsContent value="new" className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category">Kategori</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Genel</SelectItem>
                    <SelectItem value="technical">Teknik Sorun</SelectItem>
                    <SelectItem value="billing">Ödeme</SelectItem>
                    <SelectItem value="feature">Özellik İsteği</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject">Konu</Label>
                <Input
                  id="subject"
                  placeholder="Destek talebinizin konusu"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Mesaj</Label>
                <Textarea
                  id="message"
                  placeholder="Lütfen sorununuzu detaylı bir şekilde açıklayın..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  required
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Gönderiliyor..." : "Destek Talebi Gönder"}
              </Button>
            </form>
          </TabsContent>
          
          <TabsContent value="tickets" className="space-y-4">
            {loadingTickets ? (
              <div className="text-center py-8">Yükleniyor...</div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Henüz destek talebiniz bulunmuyor</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {tickets.map((ticket) => (
                  <Card key={ticket.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium">{ticket.subject}</h4>
                      <Badge variant={ticket.status === 'open' ? 'default' : 'secondary'}>
                        {ticket.status === 'open' ? 'Açık' : 'Kapalı'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{ticket.message}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Kategori: {ticket.category}</span>
                      <span>{new Date(ticket.created_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
});

SupportModal.displayName = 'SupportModal';

export default SupportModal;
